/**
 * AdminSpotlightFunnel — admin-only conversion dashboard for the
 * Featured-Slot upsell funnel (Iter 6).
 *
 * Renders the 6 canonical stages from GET /api/admin/spotlight-funnel:
 *   Lock seen → Lock clicked → Boost clicked → Pricing viewed →
 *   Plan CTA clicked → Upgraded
 *
 * Each stage shows its count and stage-to-stage CVR%. Overall CVR
 * (impression → upgrade) is called out as a headline metric.
 *
 * Filters:
 *   · Plan key (all / free / starter / pro / enterprise) — attribution
 *     scope; only events whose caller was on that plan count.
 *   · Date window (since/until) — defaults to trailing 30 days.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sparkles, RefreshCw, TrendingUp, ArrowRight } from "lucide-react";

// A "healthy" CVR benchmark used to color the CVR pill. Green if better
// than benchmark, amber if worse, muted grey if null (no denominator).
const CVR_BENCHMARK = {
    featured_lock_click: 8,   // 8% click-through on the impression
    boost_button_click: 15,   // 15% of lock-clickers also try boost
    pricing_spotlight_view: 60,  // most clicks land the page
    pricing_spotlight_cta: 20,  // 20% of viewers click a plan
    upgrade_completed: 15,   // 15% of CTA clicks close checkout
};

function cvrColor(key, cvr) {
    if (cvr == null) return "text-muted-foreground";
    const b = CVR_BENCHMARK[key];
    if (b == null) return "text-black";
    return cvr >= b ? "text-emerald-700" : "text-amber-700";
}

/** Compact colored pill for subscription-event type. Unlocked-featured
 *  upgrades get a gold treatment matching the FEATURED badge language. */
function ActivityBadge({ event, unlocked }) {
    const styles = {
        tier_upgraded: unlocked
            ? "bg-amber-100 text-amber-900 border-amber-500"
            : "bg-emerald-100 text-emerald-900 border-emerald-500",
        tier_downgraded: "bg-neutral-100 text-neutral-700 border-neutral-400",
        tier_cancelled: "bg-red-100 text-red-800 border-red-400",
        tier_renewed: "bg-blue-50 text-blue-800 border-blue-400",
    };
    const label = {
        tier_upgraded: unlocked ? "Upgraded ★" : "Upgraded",
        tier_downgraded: "Downgraded",
        tier_cancelled: "Cancelled",
        tier_renewed: "Renewed",
    }[event] || event;
    return (
        <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${styles[event] || "bg-neutral-50 text-neutral-700 border-neutral-300"}`}>
            {label}
        </span>
    );
}

function StageBar({ stage, maxCount }) {
    const pct = maxCount ? Math.max(2, Math.round((stage.n / maxCount) * 100)) : 0;
    const cvr = stage.cvr_from_prev;
    // Cap the visible CVR at 100% — anything above is a data anomaly
    // (typically caused by anonymous events that never had a preceding
    // stage, so the ratio becomes >1). Show the raw value in a tooltip.
    const cvrDisplay = cvr == null ? null : Math.min(cvr, 100);
    const cvrOutlier = cvr != null && cvr > 100;
    return (
        <div className="border border-border bg-white" data-testid={`funnel-stage-${stage.key}`}>
            <div className="flex items-center gap-3 px-4 py-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
                            {stage.label}
                        </span>
                        {cvrDisplay != null && (
                            <span
                                className={`text-[10px] font-mono uppercase tracking-widest ${cvrColor(stage.key, cvrDisplay)}`}
                                title={cvrOutlier ? `Raw CVR: ${cvr.toFixed(1)}% (capped at 100% — likely anonymous events without prior stage attribution)` : undefined}
                            >
                                {cvrDisplay.toFixed(1)}% from prev{cvrOutlier ? " ⚠" : ""}
                            </span>
                        )}
                    </div>
                    <div className="font-display font-black text-2xl sm:text-3xl mt-1 tabular-nums">
                        {stage.n.toLocaleString()}
                    </div>
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground shrink-0 hidden sm:block">
                    {stage.key}
                </div>
            </div>
            {/* Bar */}
            <div className="h-1.5 bg-neutral-100 relative overflow-hidden">
                <div
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-ikb to-amber-400 transition-all duration-500"
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    );
}

function todayISO(offsetDays = 0) {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    return d.toISOString().slice(0, 10);
}

export default function AdminSpotlightFunnel() {
    const [data, setData] = useState(null);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [planKey, setPlanKey] = useState("__all__");
    const [since, setSince] = useState(todayISO(-30));
    const [until, setUntil] = useState(todayISO(0));

    const load = async () => {
        setLoading(true);
        setErr(null);
        try {
            const params = { since, until };
            if (planKey && planKey !== "__all__") params.plan_key = planKey;
            const [{ data: funnel }, { data: activity }] = await Promise.all([
                api.get("/admin/spotlight-funnel", { params }),
                api.get("/admin/subscription-events", { params: { since, until, limit: 15 } }),
            ]);
            setData(funnel);
            setEvents(activity?.items || []);
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const stages = useMemo(() => {
        if (!data) return [];
        return (data.stages || []).map((s) => ({ ...s, n: data.counts?.[s.key] || 0 }));
    }, [data]);

    const maxCount = useMemo(() => stages.reduce((m, s) => Math.max(m, s.n || 0), 0), [stages]);

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6" data-testid="admin-funnel-page">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-ikb">
                        <Sparkles className="w-3 h-3" /> Featured-Slot Funnel
                    </div>
                    <h1 className="font-display font-black text-3xl sm:text-4xl mt-2 leading-tight">
                        Spotlight upgrade conversion
                    </h1>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-1 max-w-2xl">
                        Impression → click → boost → pricing view → CTA → checkout. Aggregated across all
                        Portalwiz tenants unless filtered by plan.
                    </p>
                </div>
                <Link
                    to="/admin"
                    className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground hover:text-black"
                    data-testid="funnel-back-to-admin"
                >
                    ← Admin panel
                </Link>
            </div>

            {/* Filter bar */}
            <div className="border border-border bg-white p-4 grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-3 sm:gap-4 items-end" data-testid="funnel-filters">
                <div>
                    <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Plan</Label>
                    <Select value={planKey} onValueChange={setPlanKey}>
                        <SelectTrigger className="rounded-none mt-1 h-9" data-testid="funnel-filter-plan">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__all__">All plans</SelectItem>
                            <SelectItem value="free">Free</SelectItem>
                            <SelectItem value="starter">Starter</SelectItem>
                            <SelectItem value="pro">Pro</SelectItem>
                            <SelectItem value="enterprise">Enterprise</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">From</Label>
                    <Input type="date" value={since} onChange={(e) => setSince(e.target.value)}
                        className="rounded-none mt-1 h-9 font-mono text-xs" data-testid="funnel-filter-since" />
                </div>
                <div>
                    <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">To</Label>
                    <Input type="date" value={until} onChange={(e) => setUntil(e.target.value)}
                        className="rounded-none mt-1 h-9 font-mono text-xs" data-testid="funnel-filter-until" />
                </div>
                <Button
                    onClick={load}
                    disabled={loading}
                    className="rounded-none h-9"
                    data-testid="funnel-apply-btn"
                >
                    <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
                    {loading ? "Loading" : "Apply"}
                </Button>
            </div>

            {err && (
                <div className="border border-red-300 bg-red-50 p-3 text-xs text-red-800" data-testid="funnel-error">
                    {err}
                </div>
            )}

            {/* Headline KPI */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <div className="border border-black bg-black text-white p-5" data-testid="funnel-overall-cvr">
                    <div className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-white/60">
                        <TrendingUp className="w-3 h-3" /> Overall CVR
                    </div>
                    <div className="font-display font-black text-4xl mt-2 tabular-nums"
                        title={data?.overall_cvr_pct != null && data.overall_cvr_pct > 100 ? `Raw: ${data.overall_cvr_pct.toFixed(1)}% (capped; upgrades > impressions is a data-attribution anomaly)` : undefined}>
                        {data?.overall_cvr_pct == null
                            ? "—"
                            : `${Math.min(data.overall_cvr_pct, 100).toFixed(1)}%${data.overall_cvr_pct > 100 ? " ⚠" : ""}`}
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mt-1">
                        impression → upgraded
                    </div>
                </div>
                <div className="border border-border bg-white p-5" data-testid="funnel-total-impressions">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Total impressions</div>
                    <div className="font-display font-black text-4xl mt-2 tabular-nums">
                        {(data?.counts?.featured_lock_impression || 0).toLocaleString()}
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-1">
                        featured_lock_impression
                    </div>
                </div>
                <div className="border border-border bg-white p-5" data-testid="funnel-upgrades">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Upgrades in window</div>
                    <div className="font-display font-black text-4xl mt-2 tabular-nums">
                        {(data?.counts?.upgrade_completed || 0).toLocaleString()}
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-1">
                        active starter+ subscriptions
                    </div>
                </div>
            </div>

            {/* Funnel + activity — 2-column on large screens */}
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
                <div className="space-y-3" data-testid="funnel-stages">
                    {stages.length === 0 && !loading && (
                        <div className="border border-dashed border-border p-8 text-center text-xs text-muted-foreground">
                            No events in this window. Try a wider date range or clear the plan filter.
                        </div>
                    )}
                    {stages.map((s, i) => (
                        <div key={s.key}>
                            <StageBar stage={s} maxCount={maxCount} />
                            {i < stages.length - 1 && (
                                <div className="h-4 flex items-center justify-center">
                                    <ArrowRight className="w-3.5 h-3.5 text-muted-foreground rotate-90" />
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Recent upgrades activity feed */}
                <aside className="border border-border bg-white" data-testid="funnel-activity-feed">
                    <div className="px-4 py-3 border-b border-border">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                            Recent activity
                        </div>
                        <div className="text-sm font-semibold mt-0.5">Subscription events</div>
                    </div>
                    <ul className="divide-y divide-border max-h-[540px] overflow-y-auto pw-thin-scroll">
                        {events.length === 0 && !loading && (
                            <li className="px-4 py-6 text-center text-xs text-muted-foreground" data-testid="funnel-activity-empty">
                                No plan changes in this window.
                            </li>
                        )}
                        {events.map((ev) => (
                            <li key={ev.id} className="px-4 py-2.5" data-testid={`funnel-activity-row-${ev.id}`}>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <ActivityBadge event={ev.event} unlocked={ev.unlocked_featured_slot} />
                                    <span className="text-xs font-medium truncate flex-1 min-w-0">
                                        {ev.company?.name || "—"}
                                    </span>
                                </div>
                                <div className="text-[10px] font-mono text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                                    <span>{ev.from_plan || "—"}</span>
                                    <ArrowRight className="w-2.5 h-2.5" />
                                    <span className="text-black">{ev.to_plan || "—"}</span>
                                    <span className="opacity-50">·</span>
                                    <span>{new Date(ev.ts).toLocaleDateString()} {new Date(ev.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                                </div>
                            </li>
                        ))}
                    </ul>
                </aside>
            </div>

            {/* Window footer */}
            {data?.window && (
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground text-right" data-testid="funnel-window">
                    Window: {data.window.since?.slice(0, 10)} → {data.window.until?.slice(0, 10)}
                    {data.plan_key_filter && ` · plan=${data.plan_key_filter}`}
                </div>
            )}
        </div>
    );
}
